import MouseMode from "../settings/MouseMode";
import React, {createRef, ReactNode, RefObject} from "react";
import {
	AddToolboxAnnotation,
	BorderStyle,
	DrawMode,
	Metrics,
	Point,
	Rectangle,
	TextAnnotationIcons,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MouseFunction from "../settings/MouseFunction";
import Immutable from "../../../shared/immutable/Immutable";
import RectSelection from "../../document/components/selection/RectSelection";
import PointSelection from "../../document/components/selection/PointSelection";
import LineSelection from "../../document/components/selection/LineSelection";
import {PortalRubberStampAnnotation} from "../../../shared/models/PortalRubberStampAnnotation";
import {PortalToolboxAnnotationAnnotation} from "../../../shared/models/PortalToolboxAnnotationAnnotation";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../shared/helper/UnitHelper";
import LengthUnit from "../../../shared/units/LengthUnit";
import {noteIconSize} from "../../document/components/pdf/annotation/AnnotationNoteFunction";
import {
	swissBillHeight,
	swissBillSpaceHeight,
	swissBillSpaceWidth,
	swissBillWidth,
	swissCodeSize
} from "../../dialog/components/barcode/BarcodeCreateSwissQrCodeAreaDialog";
import PortalDocument from "../../document/PortalDocument";
import DocumentSettings from "../../document/DocumentSettings";
import PortalDocumentManager from "../../document/PortalDocumentManager";
import {
	AreaLayerProperties,
	initAreaLayerGlobals,
	PortalOperationLineAnnotation,
	PortalOperationPoint,
	PortalOperationRectangle
} from "./AreaLayerInterface";
import {isMobile} from "react-device-detect";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";
import {initFormLayerGlobals} from "./form/FormLayerInterface";
import {initSidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";

export class AreaLayerDefinition extends PortalPureComponent<AreaLayerProperties> {
	protected areaLayer: RefObject<HTMLDivElement>;

	/**
	 * pdf page constructor
	 */
	constructor(props: Readonly<AreaLayerProperties>) {
		super(props);

		this.state = {
			areaLayer: null
		};

		this.areaLayer = createRef<HTMLDivElement>();

		this.renderRectSelection = this.renderRectSelection.bind(this);
		this.getAreaLayer = this.getAreaLayer.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<AreaLayerProperties>, prevState: Readonly<any>
	): Promise<void> {
		if (this.props.zoomValue !== prevProps.zoomValue || this.props.mouseMode !== prevProps.mouseMode ||
			this.props.mouseFunction !== prevProps.mouseFunction) {
			this.setState({});
		}
	}

	private renderRectSelection(): ReactNode {
		let functions: Array<MouseFunction> = [
			MouseFunction.REDACT_AREA,
			MouseFunction.WATERMARK,
			MouseFunction.SIGNATURE,
			MouseFunction.BARCODE_DETECT,
			MouseFunction.BARCODE_CREATE,
			MouseFunction.BARCODE_CREATE_SWISS_BILL,
			MouseFunction.BARCODE_CREATE_SWISS_CODE,
			MouseFunction.BARCODE_CREATE_SWISS_BILL_WITH_SPACE,
			MouseFunction.SQUARE,
			MouseFunction.CIRCLE,
			MouseFunction.STAMP_APPROVED,
			MouseFunction.STAMP_EXPERIMENTAL,
			MouseFunction.STAMP_NOT_APPROVED,
			MouseFunction.STAMP_AS_IS,
			MouseFunction.STAMP_EXPIRED,
			MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CONFIDENTIAL,
			MouseFunction.STAMP_FINAL,
			MouseFunction.STAMP_SOLD,
			MouseFunction.STAMP_DEPARTMENTAL,
			MouseFunction.STAMP_FOR_COMMENT,
			MouseFunction.STAMP_TOP_SECRET,
			MouseFunction.STAMP_DRAFT,
			MouseFunction.STAMP_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CUSTOM,
			MouseFunction.NOTE
		];

		if (!functions.includes(this.props.mouseFunction)) {
			return null;
		}

		let width: number | undefined;
		let height: number | undefined;
		let interiorColor: string | undefined;
		let borderColor: string | undefined;
		let borderStyle: BorderStyle | undefined;
		let borderWidth: number | undefined;
		let fontColor: string | undefined;
		let fontSize: number | undefined;
		let drawMode: DrawMode | undefined;
		let opacity: number | undefined;
		let icon: TextAnnotationIcons | undefined;
		let content: string | undefined;

		switch (this.props.mouseFunction) {
			case MouseFunction.NOTE:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let annotationNoteParameter: ToolboxAnnotationAnnotation = ToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

				if (FunctionHelper.applyValue(
					annotationNoteParameter.add?.text, AddToolboxAnnotation.getTextDefault()
				).length === 0) {
					break;
				}

				opacity = annotationNoteParameter.add!.text![0].opacity;
				interiorColor = annotationNoteParameter.add!.text![0].color;
				icon = annotationNoteParameter.add!.text![0].icon;
				width = UnitHelper.unitToPx(noteIconSize, LengthUnit.MILLIMETER, this.props.portalResolution);
				height = UnitHelper.unitToPx(noteIconSize, LengthUnit.MILLIMETER, this.props.portalResolution);
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_CODE:
				width = swissCodeSize;
				height = swissCodeSize;
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_BILL:
				width = swissBillWidth;
				height = swissBillHeight;
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_BILL_WITH_SPACE:
				width = swissBillSpaceWidth;
				height = swissBillSpaceHeight;
				break;
			case MouseFunction.SQUARE:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let annotationSquareParameter: ToolboxAnnotationAnnotation = ToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

				if (FunctionHelper.applyValue(
					annotationSquareParameter.add?.square, AddToolboxAnnotation.getSquareDefault()
				).length === 0) {
					break;
				}

				interiorColor = annotationSquareParameter.add!.square![0].interiorColor;
				borderColor = annotationSquareParameter.add!.square![0].color;
				borderStyle = annotationSquareParameter.add!.square![0].border!.borderStyle;
				borderWidth = annotationSquareParameter.add!.square![0].border!.borderWidth;
				drawMode = annotationSquareParameter.add!.square![0].drawMode;
				opacity = annotationSquareParameter.add!.square![0].opacity;
				break;
			case MouseFunction.CIRCLE:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let annotationCircleParameter: ToolboxAnnotationAnnotation = ToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

				if (FunctionHelper.applyValue(
					annotationCircleParameter.add?.circle, AddToolboxAnnotation.getCircleDefault()
				).length === 0) {
					break;
				}

				interiorColor = annotationCircleParameter.add!.circle![0].interiorColor;
				borderColor = annotationCircleParameter.add!.circle![0].color;
				borderStyle = annotationCircleParameter.add!.circle![0].border!.borderStyle;
				borderWidth = annotationCircleParameter.add!.circle![0].border!.borderWidth;
				drawMode = annotationCircleParameter.add!.circle![0].drawMode;
				opacity = annotationCircleParameter.add!.circle![0].opacity;
				break;
			case MouseFunction.REDACT_AREA:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let redactAreaParameter: ToolboxAnnotationAnnotation = ToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

				if (FunctionHelper.applyValue(
					redactAreaParameter.add?.redact, AddToolboxAnnotation.getRedactDefault()
				).length === 0) {
					break;
				}

				interiorColor = redactAreaParameter.add!.redact![0].interiorColor;
				borderColor = redactAreaParameter.add!.redact![0].color;
				opacity = redactAreaParameter.add!.redact![0].opacity;
				borderWidth = 3;
				break;
			case MouseFunction.STAMP_APPROVED:
			case MouseFunction.STAMP_EXPERIMENTAL:
			case MouseFunction.STAMP_NOT_APPROVED:
			case MouseFunction.STAMP_AS_IS:
			case MouseFunction.STAMP_EXPIRED:
			case MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE:
			case MouseFunction.STAMP_CONFIDENTIAL:
			case MouseFunction.STAMP_FINAL:
			case MouseFunction.STAMP_SOLD:
			case MouseFunction.STAMP_DEPARTMENTAL:
			case MouseFunction.STAMP_FOR_COMMENT:
			case MouseFunction.STAMP_TOP_SECRET:
			case MouseFunction.STAMP_DRAFT:
			case MouseFunction.STAMP_FOR_PUBLIC_RELEASE:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let stampParameter: PortalToolboxAnnotationAnnotation = PortalToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

				if (FunctionHelper.applyValue(
					stampParameter.add?.rubberstamp, AddToolboxAnnotation.getRubberstampDefault()
				).length === 0) {
					break;
				}

				opacity = stampParameter.add!.rubberstamp![0].opacity;
				break;
			case MouseFunction.STAMP_CUSTOM:
				if (this.props.mouseFunctionAnnotationParameter === null) {
					break;
				}

				let customStampParameter: PortalToolboxAnnotationAnnotation =
					PortalToolboxAnnotationAnnotation.fromJson(
						this.props.mouseFunctionAnnotationParameter?.get()
					);

				if (FunctionHelper.applyValue(
					customStampParameter.add?.rubberstamp, AddToolboxAnnotation.getRubberstampDefault()
				).length === 0) {
					break;
				}

				let rubberstampParameter: PortalRubberStampAnnotation = customStampParameter.add!.rubberstamp![0];
				opacity = rubberstampParameter.opacity;
				content = rubberstampParameter.text;
				interiorColor = rubberstampParameter.color;
				borderColor = rubberstampParameter.borderColor;
				fontColor = rubberstampParameter.fontColor;
				fontSize = rubberstampParameter.fontSize;
				break;
		}

		let resizeable: Array<MouseFunction> = [
			MouseFunction.WATERMARK,
			MouseFunction.REDACT_AREA,
			MouseFunction.SIGNATURE,
			MouseFunction.BARCODE_DETECT,
			MouseFunction.BARCODE_CREATE,
			MouseFunction.SQUARE,
			MouseFunction.CIRCLE,
			MouseFunction.STAMP_APPROVED,
			MouseFunction.STAMP_EXPERIMENTAL,
			MouseFunction.STAMP_NOT_APPROVED,
			MouseFunction.STAMP_AS_IS,
			MouseFunction.STAMP_EXPIRED,
			MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CONFIDENTIAL,
			MouseFunction.STAMP_FINAL,
			MouseFunction.STAMP_SOLD,
			MouseFunction.STAMP_DEPARTMENTAL,
			MouseFunction.STAMP_FOR_COMMENT,
			MouseFunction.STAMP_TOP_SECRET,
			MouseFunction.STAMP_DRAFT,
			MouseFunction.STAMP_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CUSTOM
		];

		let confirmFunctions: Array<MouseFunction> = [
			MouseFunction.BARCODE_DETECT,
			MouseFunction.BARCODE_CREATE,
			MouseFunction.BARCODE_CREATE_SWISS_CODE,
			MouseFunction.BARCODE_CREATE_SWISS_BILL,
			MouseFunction.BARCODE_CREATE_SWISS_BILL_WITH_SPACE,
			MouseFunction.WATERMARK,
			MouseFunction.SIGNATURE
		];

		if (isMobile) {
			confirmFunctions = confirmFunctions.concat([
				MouseFunction.NOTE,
				MouseFunction.SQUARE,
				MouseFunction.CIRCLE,
				MouseFunction.STAMP_APPROVED,
				MouseFunction.STAMP_EXPERIMENTAL,
				MouseFunction.STAMP_NOT_APPROVED,
				MouseFunction.STAMP_AS_IS,
				MouseFunction.STAMP_EXPIRED,
				MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE,
				MouseFunction.STAMP_CONFIDENTIAL,
				MouseFunction.STAMP_FINAL,
				MouseFunction.STAMP_SOLD,
				MouseFunction.STAMP_DEPARTMENTAL,
				MouseFunction.STAMP_FOR_COMMENT,
				MouseFunction.STAMP_TOP_SECRET,
				MouseFunction.STAMP_DRAFT,
				MouseFunction.STAMP_FOR_PUBLIC_RELEASE,
				MouseFunction.STAMP_CUSTOM
			]);
		}

		return (
			<RectSelection {...this.props} getParent={this.getAreaLayer} width={width} height={height}
						   resizeable={resizeable.includes(this.props.mouseFunction)} interiorColor={interiorColor}
						   borderColor={borderColor} borderStyle={borderStyle} borderWidth={borderWidth}
						   drawMode={drawMode} opacity={opacity} icon={icon} content={content}
						   fontColor={fontColor} fontSize={fontSize}
						   confirmation={confirmFunctions.includes(this.props.mouseFunction)}
						   startCallback={() => {
							   this.activateMouseFunction();
						   }}
						   moveCallback={(value: Rectangle | null) => {
							   let zoomScale: number = this.props.zoomValue / 100;

							   let updates: any = {
								   mouseFunctionRect: null
							   }

							   if (FunctionHelper.exists(value)) {
								   updates.mouseFunctionRect = Immutable.from({
									   page: this.props.pageNumber,
									   height: value!.height! / zoomScale,
									   width: value!.width! / zoomScale,
									   x: value!.x! / zoomScale,
									   y: value!.y! / zoomScale,
									   metrics: Metrics.Mm,
									   timestamp: new Date().getTime()
								   } as PortalOperationRectangle)
							   }

							   this.props.context.getPage().setState(updates);
						   }}
						   endCallback={(value: Rectangle | null, apply: boolean) => {
							   let zoomScale: number = this.props.zoomValue / 100;

							   let updates: any = {
								   applyMouseFunction: apply,
								   mouseFunctionRect: null
							   }

							   if (FunctionHelper.exists(value)) {
								   updates.mouseFunctionRect = Immutable.from({
									   page: this.props.pageNumber,
									   height: value!.height! / zoomScale,
									   width: value!.width! / zoomScale,
									   x: value!.x! / zoomScale,
									   y: value!.y! / zoomScale,
									   metrics: Metrics.Mm,
									   timestamp: new Date().getTime()
								   } as PortalOperationRectangle);
							   } else {
								   this.disableMouseFunction();
								   return;
							   }

							   this.props.context.getPage().setState(updates);
						   }}/>
		);
	}

	private renderPointSelection(): ReactNode {
		let functions: Array<boolean> = [
			this.props.mouseFunction === MouseFunction.ATTACHMENT
		]

		if (!functions.includes(true)) {
			return null;
		}

		let confirmFunctions: Array<MouseFunction> = [
			MouseFunction.ATTACHMENT
		];

		return (
			<PointSelection {...this.props} getParent={this.getAreaLayer}
							confirmation={confirmFunctions.includes(this.props.mouseFunction)}
							startCallback={() => {
								this.activateMouseFunction();
							}}
							moveCallback={(value: Point | null) => {
								let zoomScale: number = this.props.zoomValue / 100;

								let updates: any = {
									mouseFunctionPoint: null
								}

								if (FunctionHelper.exists(value)) {
									updates.mouseFunctionPoint = Immutable.from({
										page: this.props.pageNumber,
										x: value!.x! / zoomScale,
										y: value!.y! / zoomScale,
										timestamp: new Date().getTime()
									} as PortalOperationPoint)
								}

								this.props.context.getPage().setState(updates);
							}}
							endCallback={(value: Point | null, apply: boolean) => {
								let zoomScale: number = this.props.zoomValue / 100;

								let updates: any = {
									applyMouseFunction: apply,
									mouseFunctionPoint: null
								}

								if (FunctionHelper.exists(value)) {
									updates.mouseFunctionPoint = Immutable.from({
										page: this.props.pageNumber,
										x: value!.x! / zoomScale,
										y: value!.y! / zoomScale,
										timestamp: new Date().getTime()
									} as PortalOperationPoint);
								} else {
									this.disableMouseFunction();
									return;
								}

								this.props.context.getPage().setState(updates);
							}}/>
		);
	}

	private renderLineSelection(): ReactNode {
		let functions: Array<boolean> = [
			this.props.mouseFunction === MouseFunction.LINE
		];

		if (!functions.includes(true)) {
			return null;
		}

		let borderColor: string | undefined;
		let borderStyle: BorderStyle | undefined;
		let borderWidth: number | undefined;
		let opacity: number | undefined;

		if (this.props.mouseFunctionAnnotationParameter !== null) {
			let annotationSquareParameter: ToolboxAnnotationAnnotation =
				ToolboxAnnotationAnnotation.fromJson(
					this.props.mouseFunctionAnnotationParameter?.get()
				);

			if (FunctionHelper.applyValue(
				annotationSquareParameter.add?.line, AddToolboxAnnotation.getLineDefault()
			).length === 0) {
				return null;
			}

			borderColor = annotationSquareParameter.add!.line![0].color;
			borderStyle = annotationSquareParameter.add!.line![0].border!.borderStyle;
			borderWidth = annotationSquareParameter.add!.line![0].border!.borderWidth;
			opacity = annotationSquareParameter.add!.line![0].opacity;
		}

		let confirmFunctions: Array<MouseFunction> = [];

		if (isMobile) {
			confirmFunctions = confirmFunctions.concat([
				MouseFunction.LINE
			]);
		}

		return (
			<LineSelection {...this.props} getParent={this.getAreaLayer} borderColor={borderColor}
						   borderStyle={borderStyle} borderWidth={borderWidth} opacity={opacity}
						   confirmation={confirmFunctions.includes(this.props.mouseFunction)}
						   startCallback={() => {
							   this.activateMouseFunction();
						   }}
						   moveCallback={(value: Array<Point> | null) => {
							   let zoomScale: number = this.props.zoomValue / 100;

							   let updates: any = {
								   mouseFunctionLine: null
							   }

							   if (FunctionHelper.exists(value)) {
								   updates.mouseFunctionLine = Immutable.from({
									   page: this.props.pageNumber,
									   start: {
										   x: value![0].x! / zoomScale,
										   y: value![0].y! / zoomScale,
										   metrics: Metrics.Mm
									   },
									   end: {
										   x: value![1].x! / zoomScale,
										   y: value![1].y! / zoomScale,
										   metrics: Metrics.Mm
									   },
									   timestamp: new Date().getTime()
								   } as PortalOperationLineAnnotation)
							   }

							   this.props.context.getPage().setState(updates);
						   }}
						   endCallback={(value: Array<Point> | null, apply: boolean) => {
							   let zoomScale: number = this.props.zoomValue / 100;

							   let updates: any = {
								   applyMouseFunction: apply,
								   mouseFunctionLine: null
							   }

							   if (FunctionHelper.exists(value)) {
								   updates.mouseFunctionLine = Immutable.from({
									   page: this.props.pageNumber,
									   start: {
										   x: value![0].x! / zoomScale,
										   y: value![0].y! / zoomScale,
										   metrics: Metrics.Mm
									   },
									   end: {
										   x: value![1].x! / zoomScale,
										   y: value![1].y! / zoomScale,
										   metrics: Metrics.Mm
									   },
									   timestamp: new Date().getTime()
								   } as PortalOperationLineAnnotation);
							   } else {
								   this.disableMouseFunction();
								   return;
							   }

							   this.props.context.getPage().setState(updates);
						   }}/>
		);
	}

	public disableMouseFunction(): void {
		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.resetMouseMode();
		}

		this.props.context.documentManager.updateGlobalDocumentList();

		this.props.context.getPage().setState({
			...initAreaLayerGlobals(),
			...initFormLayerGlobals(),
			...initSidebarFunctionGlobals()
		});
	}

	public activateMouseFunction(): void {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;

		let selection: Array<PortalDocument> = documentManager.getSelection();
		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.activeViewer.mouseFunctionActive = true;
		}

		documentManager.updateGlobalDocumentList();
	}

	public getAreaLayer(): HTMLElement | null {
		return this.areaLayer.current;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let areaLayerClasses: Array<string> = ["area-layer"];

		let modes: Array<boolean> = [
			this.props.mouseMode === MouseMode.POINT,
			this.props.mouseMode === MouseMode.RECTANGLE
		];

		if (modes.includes(true)) {
			areaLayerClasses.push("is-visible");
		}

		if (this.props.interactivePage === this.props.pageNumber) {
			areaLayerClasses.push("is-interactive");
		}

		return (
			<div className={areaLayerClasses.join(" ")} ref={this.areaLayer}>
				{this.renderPointSelection()}
				{this.renderRectSelection()}
				{this.renderLineSelection()}
			</div>
		);
	}
}

const AreaLayer = React.memo(AreaLayerDefinition,
	(prevProps: Readonly<AreaLayerProperties>, nextProps: Readonly<AreaLayerProperties>) => {
		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.mouseFunction !== prevProps.mouseFunction) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.interactivePage !== prevProps.interactivePage) {
			return false;
		}

		if (!prevProps.mouseFunctionAnnotationParameter?.equals(nextProps.mouseFunctionAnnotationParameter)) {
			return false;
		}

		return true;
	}
);

export default AreaLayer;